import React, { useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconClose from '@/assets/icons/icon-close.svg?react';
import upgradeImage from '@/assets/images/upgrade-now-to-publish.png';
import { useMixpanel } from '@/hooks/useMixpanel';
import { useHubSpot, HUBSPOT_EVENT } from '@/hooks/useHubSpot';
import { EVENT } from '@/analytics/mixpanel';
import { useAppSelector } from '@/store/hooks';
import { selectUser } from '@/store/user/selectors';

const UPGRADE_URL = `${import.meta.env.VITE_API_MAIN_APP}/my-account/billing?upgrade=1`;

interface UpgradeToPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UpgradeToPublishModal: React.FC<UpgradeToPublishModalProps> = ({
  isOpen,
  onClose,
}) => {
  const track = useMixpanel();
  const trackHubSpot = useHubSpot();
  const user = useAppSelector(selectUser);
  const planName = user?.subscriptionLevel ?? 'Standard';
  const limit = user?.featureAccess?.leadpageLimit ?? 5;

  useEffect(() => {
    if (isOpen) {
      track(EVENT.UPGRADE_MODAL_SHOWN);
    }
  }, [isOpen, track]);

  const handleUpgrade = () => {
    track(EVENT.UPGRADE_MODAL_UPGRADE_CLICKED);
    trackHubSpot(HUBSPOT_EVENT.UPGRADE_MODAL_UPGRADE_CLICKED);
    window.open(UPGRADE_URL, '_blank');
    onClose();
  };

  const handleDismiss = () => {
    track(EVENT.UPGRADE_MODAL_DISMISSED);
    trackHubSpot(HUBSPOT_EVENT.UPGRADE_MODAL_DISMISSED);
    onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleDismiss()}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-100001 bg-black/50" />
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed top-1/2 left-1/2 z-100001 w-[384px] max-w-[95vw] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-[0.80px] outline-offset-[-0.80px] outline-neutral-100">
          <VisuallyHidden.Root>
            <Dialog.Description>Upgrade your plan to publish more landing pages</Dialog.Description>
          </VisuallyHidden.Root>

          {/* Header */}
          <div className="flex h-10 items-center justify-between border-b border-neutral-100 pr-2 pl-3">
            <Dialog.Title className="text-xs font-medium text-neutral-900">
              Upgrade Now to Publish
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconSimpleButton
                icon={IconClose}
                size={24}
                aria-label="Close"
                data-qa-selector="upgrade-modal-close-button"
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-3 px-3 pb-2">
            <img
              src={upgradeImage}
              alt="Upgrade to publish more pages"
              className="w-full rounded"
            />
            <p className="text-xs leading-4 font-medium text-neutral-500">
              The {planName} plan includes {limit} published landing pages. Upgrade to publish more
              pages.
            </p>
            <div className="flex gap-1">
              <SecondaryButton
                icon={null}
                size="sm"
                text="Maybe later"
                className="h-9 flex-1"
                onClick={handleDismiss}
                data-qa-selector="upgrade-modal-dismiss-button"
              />
              <PrimaryButton
                icon={null}
                size="sm"
                text="Upgrade Plan"
                className="h-9 flex-1"
                onClick={handleUpgrade}
                data-qa-selector="upgrade-modal-upgrade-button"
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
